import React from 'react';

import OAuthLogin from '../../components/loginpages/OAuthLogin';
import { LoginComponentProps } from '../../types/LoginFormData';
import { TSPCategory, TSPConfig, createOAuthHelpText } from '../types';
import { OAuthHandlers } from '../types';

// Per Motive scopes: if a resource offers both *.read and *.manage, request only one—prefer *.manage
// when write is needed; never list both for the same prefix (e.g. not dispatch_locations.read+…manage).
const MOTIVE_BASE_DEFAULT =
  'companies.read+users.manage+vehicles.manage+messages.manage+fuel_purchases.manage+eld_devices.read+inspection_reports.read+fault_codes.read+hos_logs.available_time+hos_logs.hours_of_service+hos_logs.hos_violation+hos_logs.logs+hos_logs.manage+locations.vehicle_locations_list+locations.vehicle_locations_single+locations.driver_locations+locations.asset_locations+ifta_reports.trips+ifta_reports.summary+utilization.driver_utilization+utilization.vehicle_utilization+utilization.idle_events+documents.read+driver_performance_events.read+assets.manage+motive_card_transactions.read+motive_card_cards.read';

const MOTIVE_BASE_ATOB =
  'companies.read+users.read+hos_logs.available_time+hos_logs.hours_of_service+hos_logs.hos_violation+hos_logs.logs+vehicles.read+locations.vehicle_locations_list+locations.vehicle_locations_single+locations.driver_locations+assets.read+locations.asset_locations+fuel_purchases.manage+ifta_reports.summary';

/**
 * Extra scopes for select services. Dispatch APIs expose both *.read and *.manage in Motive docs;
 * *.manage covers write (and typically read paths) — request manage only, not read+manage together.
 * Forms / form_entries are read-only in Motive’s scope list, so *.read only.
 */
const MOTIVE_DISPATCH_SCOPES =
  'forms.read+form_entries.read+dispatch_locations.manage+dispatches.manage';

const SERVICE_NAMES_FOR_DISPATCH_SCOPES = [
  'over the road',
  'catena clearing',
  'revenova',
] as const;

const motiveConfig: TSPConfig = {
  key: 'motive',
  name: 'Motive',
  category: TSPCategory.OAUTH,

  // OAuth configuration
  oauth: {
    authUrl: 'https://api.gomotive.com/oauth/authorize',
    requiredParams: ['client_id', 'response_type', 'scope', 'redirect_uri'],
    getRedirectUri: (linkState: string) =>
      `${process.env.REACT_APP_REDIRECT_URL}/motive?state=${linkState}`,
    getScope: ({ serviceName }) => {
      const base =
        serviceName === 'AtoB' ? MOTIVE_BASE_ATOB : MOTIVE_BASE_DEFAULT;
      const lower = serviceName.toLowerCase();
      const addDispatch = SERVICE_NAMES_FOR_DISPATCH_SCOPES.some((fragment) =>
        lower.includes(fragment)
      );
      let scope = addDispatch ? `${base}+${MOTIVE_DISPATCH_SCOPES}` : base;

      // Webhooks scope only for Catena Clearing in staging (matches REACT_APP_NODE_ENV in index.tsx).
      const isStaging = process.env.REACT_APP_NODE_ENV === 'staging';
      if (isStaging && lower.includes('catena clearing')) {
        scope = `${scope}+company_webhooks.manage`;
      }
      return scope;
    },
    // Motive-specific URL building: requires literal '+' characters in scopes (no URL encoding)
    urlBuildingOptions: {
      disableScopeEncoding: true,
    },
    // API field mappings for the onboard endpoint
    callbackOptions: {
      apiFieldMappings: {
        redirectUriField: 'motive_redirect_uri',
      },
    },
  },

  helpText: createOAuthHelpText('Motive'),
  loginComponent: (
    props: LoginComponentProps,
    oAuthHandlers?: OAuthHandlers
  ) => {
    return (
      <OAuthLogin
        onDirectConnect={() => {
          oAuthHandlers?.initiateOAuth('motive');
        }}
        tsp='motive'
        serviceName={props.serviceName || ''}
        onValidityChange={props.onValidityChange}
      />
    );
  },
};

export default motiveConfig;
